import {
  deriveAsciiLabel,
  normaliseAddressQuery,
  type StreetKitBuiltInFieldKey,
  type StreetKitCustomFieldMapping,
  type StreetKitError,
  type StreetKitFieldControl,
  type StreetKitFieldMap,
  type StreetKitFieldMappingContext,
  type StreetKitFieldMappingOptions,
  type StreetKitFieldOverwriteMode,
  type StreetKitFieldTarget,
  type StreetKitSearchCompleteEvent,
  type StreetKitSearchCompleteStatus,
  type StreetKitSearchStartEvent,
  type StreetKitSearchResult,
  type StreetKitSelectedAddress
} from "@streetkit/shared";

import {
  DEFAULT_STREETKIT_DEBOUNCE_MS,
  DEFAULT_STREETKIT_LIMIT,
  MAX_STREETKIT_RENDERED_SUGGESTIONS,
  StreetKitConfigError,
  StreetKitHttpError,
  StreetKitIndexClient,
  normaliseQueryCacheKey,
  normaliseQueryForLength,
  validateStreetKitSiteId,
  type StreetKitClientConfig,
  type StreetKitSearchPlan,
  type StreetKitSearchOptions
} from "./fetch-index.js";
import { toPublicSearchResult, type RankedStreetKitCandidate } from "./rank.js";
import {
  createStreetKitDeliveryZoneLookup,
  evaluateStreetKitRadius,
  hasStreetKitSelectionContext,
  type StreetKitDeliveryZonesConfig,
  type StreetKitDriveZonesConfig,
  type StreetKitDeliveryZoneLookupClient,
  type StreetKitRadiusRulesConfig,
  type StreetKitSelectionContext
} from "./selection-context.js";

export type StreetKitWidgetConfig = StreetKitClientConfig & {
  input: string | HTMLInputElement;
  debounceMs?: number;
  limit?: number;
  maxResults?: number;
  minLength?: number;
  minQueryLength?: number;
  ascii?: boolean;
  highlight?: boolean;
  clearMappedFieldsOnClear?: boolean;
  onQuery?: (event: StreetKitQueryEvent) => void;
  onResults?: (event: StreetKitResultsEvent) => void;
  onSearchStart?: (event: StreetKitSearchStartEvent) => void;
  onSearchComplete?: (event: StreetKitSearchCompleteEvent) => void;
  onSelect?: (address: StreetKitSelectedAddress, context?: StreetKitSelectionContext) => void;
  onClear?: (event: StreetKitClearEvent) => void;
  onError?: (error: StreetKitError) => void;
  fields?: StreetKitFieldConfig;
  fieldMapping?: StreetKitFieldMappingOptions;
  radius?: StreetKitRadiusRulesConfig;
  radiusRules?: StreetKitRadiusRulesConfig;
  deliveryZones?: StreetKitDeliveryZonesConfig;
  driveZones?: StreetKitDriveZonesConfig;
  classes?: Partial<StreetKitWidgetClasses>;
};

export type StreetKitWidget = {
  input: HTMLInputElement;
  search(query: string): Promise<StreetKitSearchResult[]>;
  open(): void;
  close(): void;
  clear(): void;
  destroy(): void;
};

export type StreetKitQueryEvent = {
  rawQuery: string;
  normalizedQuery: string;
  normalisedQuery: string;
  requestId: number;
};

export type StreetKitResultsEvent = StreetKitSearchCompleteEvent & {
  plan: StreetKitSearchPlan;
  planning: StreetKitSearchPlan;
};

export type StreetKitClearEvent = {
  input: HTMLInputElement;
  reason: "api" | "user";
};

export type StreetKitFieldConfig = StreetKitFieldMap;

export type StreetKitWidgetClasses = {
  host: string;
  listbox: string;
  liveRegion: string;
  option: string;
  activeOption: string;
  status: string;
  loading: string;
  empty: string;
  error: string;
  title: string;
  meta: string;
  highlight: string;
};

const defaultClasses: StreetKitWidgetClasses = {
  host: "sk-widget-host",
  listbox: "sk-listbox",
  liveRegion: "sk-live-region",
  option: "sk-option",
  activeOption: "sk-option-active",
  status: "sk-status",
  loading: "sk-loading",
  empty: "sk-empty",
  error: "sk-error",
  title: "sk-option-title",
  meta: "sk-option-meta",
  highlight: "sk-highlight"
};

let widgetId = 0;
const activeWidgets = new WeakMap<HTMLInputElement, StreetKitWidgetController>();

export class StreetKitWidgetController implements StreetKitWidget {
  readonly input: HTMLInputElement;

  private readonly config: StreetKitWidgetConfig;
  private readonly client: StreetKitIndexClient;
  private readonly document: Document;
  private readonly classes: StreetKitWidgetClasses;
  private readonly deliveryZoneLookup: StreetKitDeliveryZoneLookupClient | null;
  private readonly listbox: HTMLDivElement;
  private readonly listboxId: string;
  private readonly liveRegion: HTMLDivElement;
  private readonly liveRegionId: string;
  private readonly previousInputAttributes: Map<string, string | null>;
  private readonly hostElement: HTMLElement | null;
  private readonly hostHadClass: boolean;
  private readonly limit: number;
  private readonly configuredMinQueryLength: number | undefined;
  private readonly debounceMs: number;
  private readonly ascii: boolean;
  private readonly highlight: boolean;
  private readonly clearMappedFieldsOnClear: boolean;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private blurTimer: ReturnType<typeof setTimeout> | null = null;
  private activeSearchController: AbortController | null = null;
  private lastScheduledSearchKey: string | null = null;
  private requestId = 0;
  private activeIndex = -1;
  private results: RankedStreetKitCandidate[] = [];
  private selectedInputValue: string | null = null;
  private readonly fieldWrittenValues = new Map<StreetKitFieldControl, string>();
  private readonly fieldMappingWarnings = new Set<string>();
  private readonly configWarnings = new Set<string>();
  private destroyed = false;

  private readonly handleInput = (): void => {
    if (this.syncDisabledState()) {
      this.results = [];
      this.abortActiveSearch();
      this.renderStatus("Address search is disabled.", this.classes.empty);
      this.close();
      return;
    }

    if (this.selectedInputValue !== null && this.input.value !== this.selectedInputValue) {
      this.selectedInputValue = null;
      this.clearMappedFieldsForInputChange();
      this.config.onClear?.({ input: this.input, reason: "user" });
    }

    this.scheduleSearch();
  };

  private readonly handleFocus = (): void => {
    if (this.blurTimer !== null) {
      clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }

    if (this.syncDisabledState()) {
      this.close();
      return;
    }

    if (this.results.length > 0) {
      this.renderOptions();
      this.open();
      return;
    }

    this.scheduleSearch(0);
  };

  private readonly handleBlur = (): void => {
    this.blurTimer = setTimeout(() => {
      this.close();
    }, 120);
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (this.syncDisabledState()) {
      return;
    }

    if (event.key === "Tab") {
      this.close();
      return;
    }

    if (event.key === "Escape") {
      if (!this.listbox.hidden) {
        event.preventDefault();
      }

      this.close();
      return;
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") {
      return;
    }

    if (event.key === "Enter") {
      if (this.activeIndex >= 0 && this.activeIndex < this.results.length) {
        event.preventDefault();
        void this.selectIndex(this.activeIndex);
      }

      return;
    }

    if (this.results.length === 0) {
      return;
    }

    event.preventDefault();
    const direction = event.key === "ArrowDown" ? 1 : -1;
    const nextIndex =
      this.activeIndex === -1
        ? direction === 1
          ? 0
          : this.results.length - 1
        : clamp(this.activeIndex + direction, 0, this.results.length - 1);

    this.setActiveIndex(nextIndex);
    this.open();
  };

  private readonly handleDocumentPointerDown = (event: PointerEvent): void => {
    const target = event.target;

    if (!(target instanceof Node)) {
      return;
    }

    if (target === this.input || this.listbox.contains(target)) {
      return;
    }

    this.close();
  };

  constructor(config: StreetKitWidgetConfig) {
    this.input = resolveValidatedInput(config);
    this.config = config;
    this.document = this.input.ownerDocument;
    this.classes = { ...defaultClasses, ...config.classes };
    this.client = createIndexClient(config);
    this.deliveryZoneLookup = createStreetKitDeliveryZoneLookup(config);
    this.limit = normaliseRenderedLimit(config.limit ?? config.maxResults);
    this.configuredMinQueryLength = config.minQueryLength ?? config.minLength;
    this.debounceMs = config.debounceMs ?? DEFAULT_STREETKIT_DEBOUNCE_MS;
    this.ascii = config.ascii === true;
    this.highlight = config.highlight !== false;
    this.clearMappedFieldsOnClear = config.clearMappedFieldsOnClear !== false;
    const instanceId = ++widgetId;
    this.listboxId = `streetkit-listbox-${instanceId}`;
    this.liveRegionId = `streetkit-status-${instanceId}`;
    this.previousInputAttributes = captureInputAttributes(this.input);
    this.hostElement = this.input.parentElement;
    this.hostHadClass = this.hostElement?.classList.contains(this.classes.host) ?? false;
    this.listbox = this.createListbox();
    this.liveRegion = this.createLiveRegion();

    this.attach();
    this.renderStatus(
      this.syncDisabledState() ? "Address search is disabled." : "Type to search for an address."
    );
    this.close();
  }

  async search(query: string): Promise<StreetKitSearchResult[]> {
    const options: StreetKitSearchOptions = { limit: this.limit };

    if (this.configuredMinQueryLength !== undefined) {
      options.minQueryLength = this.configuredMinQueryLength;
    }

    const response = await this.client.searchCandidatesWithPlan(query, options);
    return response.candidates.map(toPublicSearchResult);
  }

  open(): void {
    if (this.destroyed || this.syncDisabledState() || this.listbox.childElementCount === 0) {
      return;
    }

    this.listbox.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }

  close(): void {
    this.listbox.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    this.clearActiveIndex();
  }

  clear(): void {
    this.results = [];
    this.activeIndex = -1;
    this.selectedInputValue = null;
    this.input.value = "";
    this.lastScheduledSearchKey = null;
    this.abortActiveSearch();
    this.clearMappedFields();
    this.renderStatus("Type to search for an address.");
    this.close();
    this.config.onClear?.({ input: this.input, reason: "api" });
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.clearTimers();
    this.abortActiveSearch();
    this.input.removeEventListener("input", this.handleInput);
    this.input.removeEventListener("focus", this.handleFocus);
    this.input.removeEventListener("blur", this.handleBlur);
    this.input.removeEventListener("keydown", this.handleKeyDown);
    this.document.removeEventListener("pointerdown", this.handleDocumentPointerDown);
    restoreInputAttributes(this.input, this.previousInputAttributes);
    if (!this.hostHadClass) {
      this.hostElement?.classList.remove(this.classes.host);
    }
    this.listbox.remove();
    this.liveRegion.remove();
    activeWidgets.delete(this.input);
  }

  private attach(): void {
    activeWidgets.set(this.input, this);
    this.input.autocomplete = "off";
    this.input.setAttribute("autocomplete", "off");
    this.input.setAttribute("role", "combobox");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.setAttribute("aria-haspopup", "listbox");
    this.input.setAttribute("aria-controls", this.listboxId);
    this.input.setAttribute("aria-expanded", "false");
    this.syncDisabledState();
    this.hostElement?.classList.add(this.classes.host);
    this.input.insertAdjacentElement("afterend", this.listbox);
    this.listbox.insertAdjacentElement("afterend", this.liveRegion);
    this.input.addEventListener("input", this.handleInput);
    this.input.addEventListener("focus", this.handleFocus);
    this.input.addEventListener("blur", this.handleBlur);
    this.input.addEventListener("keydown", this.handleKeyDown);
    this.document.addEventListener("pointerdown", this.handleDocumentPointerDown);
  }

  private createListbox(): HTMLDivElement {
    const listbox = this.document.createElement("div");
    listbox.id = this.listboxId;
    listbox.className = this.classes.listbox;
    listbox.setAttribute("role", "listbox");
    listbox.setAttribute("aria-label", "Address suggestions");
    return listbox;
  }

  private createLiveRegion(): HTMLDivElement {
    const liveRegion = this.document.createElement("div");
    liveRegion.id = this.liveRegionId;
    liveRegion.className = this.classes.liveRegion;
    liveRegion.setAttribute("role", "status");
    liveRegion.setAttribute("aria-live", "polite");
    liveRegion.setAttribute("aria-atomic", "true");
    return liveRegion;
  }

  private scheduleSearch(delay = this.debounceMs): void {
    if (this.destroyed) {
      return;
    }

    if (this.syncDisabledState()) {
      this.results = [];
      this.abortActiveSearch();
      this.renderStatus("Address search is disabled.", this.classes.empty);
      this.close();
      return;
    }

    const searchKey = normaliseQueryCacheKey(this.input.value);

    if (searchKey === this.lastScheduledSearchKey) {
      return;
    }

    this.lastScheduledSearchKey = searchKey;
    const currentRequestId = ++this.requestId;
    this.abortActiveSearch();

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      void this.runSearch(this.input.value, currentRequestId);
    }, delay);
  }

  private async runSearch(rawQuery: string, currentRequestId: number): Promise<void> {
    if (this.destroyed || currentRequestId !== this.requestId) {
      return;
    }

    const normalizedQuery = normaliseAddressQuery(rawQuery);
    const queryEvent = {
      rawQuery,
      normalizedQuery,
      normalisedQuery: normalizedQuery,
      requestId: currentRequestId
    };

    this.config.onQuery?.(queryEvent);

    const queryLength = normaliseQueryForLength(rawQuery).length;

    if (queryLength === 0) {
      this.results = [];
      this.activeIndex = -1;
      this.renderStatus("Type to search for an address.");
      this.emitResults(rawQuery, normalizedQuery, currentRequestId, "empty", []);
      this.close();
      return;
    }

    if (
      this.configuredMinQueryLength !== undefined &&
      queryLength < this.configuredMinQueryLength
    ) {
      this.results = [];
      this.activeIndex = -1;
      this.renderStatus(`Type at least ${this.configuredMinQueryLength} characters to search.`);
      this.emitResults(rawQuery, normalizedQuery, currentRequestId, "too_short", []);
      this.open();
      return;
    }

    let minQueryLength = this.configuredMinQueryLength;

    if (minQueryLength === undefined) {
      try {
        const info = await this.client.getIndexInfo();

        if (this.destroyed || currentRequestId !== this.requestId) {
          return;
        }

        minQueryLength = info.minQueryLength;
      } catch (error) {
        if (this.destroyed || currentRequestId !== this.requestId) {
          return;
        }

        this.results = [];
        this.activeIndex = -1;
        this.renderStatus("Address search is unavailable. You can continue typing.", this.classes.error);
        this.open();
        const streetkitError = streetKitErrorForSearch(error, currentRequestId);
        this.warnForConfigError(streetkitError);
        this.config.onError?.(streetkitError);
        this.emitResults(rawQuery, normalizedQuery, currentRequestId, "error", []);
        return;
      }
    }

    if (queryLength < minQueryLength) {
      this.results = [];
      this.activeIndex = -1;
      this.renderStatus(`Type at least ${minQueryLength} characters to search.`);
      this.emitResults(rawQuery, normalizedQuery, currentRequestId, "too_short", []);
      this.open();
      return;
    }

    const abortController = new AbortController();
    this.activeSearchController = abortController;
    this.config.onSearchStart?.({
      rawQuery,
      normalizedQuery,
      normalisedQuery: normalizedQuery,
      requestId: currentRequestId
    });
    this.renderStatus("Searching for addresses.", this.classes.loading);
    this.open();

    try {
      const options: StreetKitSearchOptions = {
        limit: this.limit,
        minQueryLength,
        signal: abortController.signal
      };
      const response = await this.client.searchCandidatesWithPlan(rawQuery, options);
      const results = response.candidates;

      if (
        this.destroyed ||
        currentRequestId !== this.requestId ||
        abortController.signal.aborted
      ) {
        return;
      }

      this.results = results;
      this.activeIndex = -1;
      const publicResults = results.map((result) => this.toRenderedSearchResult(result));
      this.emitResults(
        rawQuery,
        normalizedQuery,
        currentRequestId,
        statusForPlan(response.plan, publicResults),
        publicResults,
        response.plan
      );

      if (results.length === 0) {
        this.renderStatus(
          response.plan.status === "too_broad"
            ? response.plan.message
            : "No matching address found.",
          response.plan.status === "too_broad" ? this.classes.empty : this.classes.empty
        );
      } else {
        this.renderOptions();
      }

      this.open();
    } catch (error) {
      if (
        this.destroyed ||
        currentRequestId !== this.requestId ||
        isAbortError(error)
      ) {
        return;
      }

      this.results = [];
      this.activeIndex = -1;
      this.renderStatus("Address search is unavailable. You can continue typing.", this.classes.error);
      this.open();
      const streetkitError = streetKitErrorForSearch(error, currentRequestId);
      this.warnForConfigError(streetkitError);
      this.config.onError?.(streetkitError);
      this.emitResults(rawQuery, normalizedQuery, currentRequestId, "error", []);
    } finally {
      if (this.activeSearchController === abortController) {
        this.activeSearchController = null;
      }
    }
  }

  private renderStatus(message: string, extraClass?: string): void {
    const status = this.document.createElement("div");
    this.clearActiveIndex();
    this.listbox.setAttribute("aria-busy", extraClass === this.classes.loading ? "true" : "false");
    status.className =
      extraClass === undefined ? this.classes.status : `${this.classes.status} ${extraClass}`;
    status.setAttribute("role", "presentation");
    status.textContent = message;
    this.announce(message);
    this.listbox.replaceChildren(status);
  }

  private renderOptions(): void {
    const fragment = this.document.createDocumentFragment();
    this.listbox.setAttribute("aria-busy", "false");

    if (this.activeIndex >= this.results.length) {
      this.clearActiveIndex();
    }

    this.results.forEach((result, index) => {
      const option = this.document.createElement("button");
      const title = this.document.createElement("span");
      const meta = this.document.createElement("span");

      option.id = this.optionId(index);
      option.type = "button";
      option.className = this.classes.option;
      option.tabIndex = -1;
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", index === this.activeIndex ? "true" : "false");
      option.setAttribute("aria-posinset", String(index + 1));
      option.setAttribute("aria-setsize", String(this.results.length));
      option.classList.toggle(this.classes.activeOption, index === this.activeIndex);
      option.addEventListener("pointerdown", (event) => {
        event.preventDefault();
      });
      option.addEventListener("mouseenter", () => {
        this.setActiveIndex(index);
      });
      option.addEventListener("click", () => {
        void this.selectIndex(index);
      });

      title.className = this.classes.title;
      appendRenderedText({
        document: this.document,
        element: title,
        text: this.displayText(result.label),
        query: this.input.value,
        highlight: this.highlight,
        highlightClass: this.classes.highlight
      });

      meta.className = this.classes.meta;
      meta.textContent = this.displayText(
        [
          result.record.components.suburb,
          result.record.components.city,
          result.record.components.territorialAuthority,
          result.record.components.postcode
        ]
          .filter(hasText)
          .join(" | ")
      );

      option.append(title, meta);
      fragment.append(option);
    });

    this.listbox.replaceChildren(fragment);
    this.announce(
      `${this.results.length} address ${
        this.results.length === 1 ? "suggestion" : "suggestions"
      } available.`
    );
  }

  private async selectIndex(index: number): Promise<void> {
    const result = this.results[index];

    if (result === undefined) {
      return;
    }

    this.input.value = this.displayText(result.label);
    this.selectedInputValue = this.input.value;
    this.close();

    try {
      const address = await this.client.getDetailForRecord(result.record, result.score);

      if (this.destroyed) {
        return;
      }

      this.populateFields(address);
      const context = await this.buildSelectionContext(address);

      if (this.destroyed) {
        return;
      }

      if (hasStreetKitSelectionContext(context)) {
        this.config.onSelect?.(address, context);
      } else {
        this.config.onSelect?.(address);
      }
    } catch (error) {
      this.config.onError?.(streetKitError("detail_failed", "Selected address detail is unavailable.", error));
      this.renderStatus("Selected address detail is unavailable. You can continue typing.", this.classes.error);
      this.open();
    }
  }

  private async buildSelectionContext(
    address: StreetKitSelectedAddress
  ): Promise<StreetKitSelectionContext> {
    const context: StreetKitSelectionContext = {};
    const radius = evaluateStreetKitRadius(
      address,
      this.config.radiusRules ?? this.config.radius
    );

    if (radius !== undefined) {
      context.radius = radius;
    }

    if (this.deliveryZoneLookup !== null) {
      const delivery = await this.deliveryZoneLookup.lookup(address);
      context.delivery = delivery;
      context.driveZones = delivery;
    }

    return context;
  }

  private setActiveIndex(index: number): void {
    const options = [...this.listbox.querySelectorAll(`.${this.classes.option}`)];

    if (index < 0 || index >= options.length) {
      this.clearActiveIndex();
      return;
    }

    this.activeIndex = index;
    const activeOption = options[index];

    options.forEach((element, optionIndex) => {
      element.classList.toggle(this.classes.activeOption, optionIndex === index);
      element.setAttribute("aria-selected", optionIndex === index ? "true" : "false");
    });

    if (activeOption === undefined || activeOption.id.length === 0) {
      this.clearActiveIndex();
      return;
    }

    this.input.setAttribute("aria-activedescendant", activeOption.id);
    activeOption.scrollIntoView?.({ block: "nearest" });
  }

  private clearActiveIndex(): void {
    this.activeIndex = -1;
    this.input.removeAttribute("aria-activedescendant");

    [...this.listbox.querySelectorAll(`.${this.classes.option}`)].forEach((element) => {
      element.classList.remove(this.classes.activeOption);
      element.setAttribute("aria-selected", "false");
    });
  }

  private announce(message: string): void {
    this.liveRegion.textContent = message;
  }

  private syncDisabledState(): boolean {
    if (this.input.disabled) {
      this.input.setAttribute("aria-disabled", "true");
      return true;
    }

    const previousAriaDisabled = this.previousInputAttributes.get("aria-disabled");

    if (previousAriaDisabled === null || previousAriaDisabled === undefined) {
      this.input.removeAttribute("aria-disabled");
      return false;
    }

    this.input.setAttribute("aria-disabled", previousAriaDisabled);
    return previousAriaDisabled === "true";
  }

  private emitResults(
    query: string,
    normalizedQuery: string,
    requestId: number,
    status: StreetKitSearchCompleteStatus,
    results: StreetKitSearchResult[],
    plan: StreetKitSearchPlan = {
      status: results.length === 0 ? "no_route" : "search",
      message: results.length === 0 ? "No matching address found." : "Search route selected.",
      minQueryLength: this.configuredMinQueryLength ?? 0,
      shardKeys: [],
      broadTerms: [],
      routeTerms: []
    }
  ): void {
    const event: StreetKitResultsEvent = {
      rawQuery: query,
      query,
      normalizedQuery,
      normalisedQuery: normalizedQuery,
      requestId,
      status,
      results,
      plan,
      planning: plan
    };

    this.config.onSearchComplete?.(event);
    this.config.onResults?.(event);
  }

  private populateFields(address: StreetKitSelectedAddress): void {
    const fields = this.config.fields;

    if (fields === undefined) {
      return;
    }

    const values = buildFieldValues(address, {
      ascii: this.ascii,
      lineMode: this.config.fieldMapping?.lineMode ?? "auto",
      separateUnit:
        fieldTargetConfigured(fields.line2) ||
        fieldTargetConfigured(fields.unit)
    });
    const context: StreetKitFieldMappingContext = { address, values };

    for (const key of streetKitBuiltInFieldKeys) {
      this.writeConfiguredField({
        key,
        target: fields[key],
        value: values[key]
      });
    }

    for (const [index, mapping] of (fields.custom ?? []).entries()) {
      this.writeCustomField(mapping, context, index);
    }
  }

  private clearMappedFields(): void {
    if (this.clearMappedFieldsOnClear === false) {
      return;
    }

    this.clearMappedFieldsByCategory("all");
  }

  private clearMappedFieldsForInputChange(): void {
    const clearOnInputChange = this.config.fieldMapping?.clearOnInputChange ?? "metadata";

    if (clearOnInputChange === "none") {
      return;
    }

    this.clearMappedFieldsByCategory(clearOnInputChange);
  }

  private clearMappedFieldsByCategory(category: "metadata" | "all"): void {
    const fields = this.config.fields;

    if (fields === undefined) {
      return;
    }

    const cleared = new Set<StreetKitFieldControl>();

    for (const key of streetKitBuiltInFieldKeys) {
      const target = fields[key];

      if (!fieldTargetConfigured(target)) {
        continue;
      }

      if (category === "metadata" && !metadataFieldKeys.has(key)) {
        continue;
      }

      this.clearConfiguredField(key, target, cleared);
    }

    for (const [index, mapping] of (fields.custom ?? []).entries()) {
      if (
        category === "metadata" &&
        (typeof mapping.value !== "string" || !metadataFieldKeys.has(mapping.value))
      ) {
        continue;
      }

      this.clearConfiguredField(`custom.${index}`, mapping, cleared);
    }
  }

  private writeConfiguredField(options: {
    key: StreetKitBuiltInFieldKey;
    target: StreetKitFieldTarget | undefined;
    value: string;
  }): void {
    const target = normaliseFieldTarget(options.target);

    if (target === null) {
      return;
    }

    this.writeFieldTarget({
      key: options.key,
      target,
      value: options.value,
      defaultOverwrite: this.config.fieldMapping?.overwrite ?? "emptyOrStreetKit"
    });
  }

  private writeCustomField(
    mapping: StreetKitCustomFieldMapping,
    context: StreetKitFieldMappingContext,
    index: number
  ): void {
    let mappedValue: string | number | null | undefined;
    const key = `custom.${index}`;

    try {
      mappedValue =
        typeof mapping.value === "function" ? mapping.value(context) : context.values[mapping.value];
    } catch (error) {
      this.warnFieldMapping({
        key,
        target: mapping.target,
        reason: "custom_mapper_failed",
        message: "StreetKit field mapping custom value function failed.",
        cause: error
      });
      return;
    }

    if (mappedValue === undefined) {
      return;
    }

    const value =
      mappedValue === null
        ? ""
        : this.ascii && typeof mappedValue === "string"
          ? deriveAsciiLabel(mappedValue)
          : String(mappedValue);

    this.writeFieldTarget({
      key,
      target: mapping,
      value,
      defaultOverwrite: this.config.fieldMapping?.overwrite ?? "emptyOrStreetKit"
    });
  }

  private writeFieldTarget(options: {
    key: string;
    target: NormalisedFieldTarget;
    value: string;
    defaultOverwrite: StreetKitFieldOverwriteMode;
  }): void {
    const element = this.resolveFieldTarget(options.key, options.target.target);

    if (element === null) {
      return;
    }

    const writeCheck = this.canWriteField(element, options);

    if (!writeCheck.allowed) {
      if (writeCheck.dropOwnership) {
        this.fieldWrittenValues.delete(element);
      }

      return;
    }

    if (element instanceof HTMLSelectElement && !selectHasOptionValue(element, options.value)) {
      this.warnFieldMapping({
        key: options.key,
        target: options.target.target,
        reason: "select_option_not_found",
        message: "StreetKit field mapping select target does not contain the mapped option."
      });
      return;
    }

    if (element.value !== options.value) {
      element.value = options.value;
      dispatchFieldEvent(element, "input");
      dispatchFieldEvent(element, "change");
    }

    this.fieldWrittenValues.set(element, options.value);
  }

  private clearConfiguredField(
    key: string,
    targetConfig: StreetKitFieldTarget | StreetKitCustomFieldMapping,
    cleared: Set<StreetKitFieldControl>
  ): void {
    const target = normaliseFieldTarget(targetConfig);

    if (target === null || target.clear === false) {
      return;
    }

    const element = this.resolveFieldTarget(key, target.target);

    if (element === null || cleared.has(element)) {
      return;
    }

    const lastValue = this.fieldWrittenValues.get(element);

    if (lastValue === undefined) {
      return;
    }

    if (element.value !== lastValue) {
      this.fieldWrittenValues.delete(element);
      return;
    }

    if (element.disabled || (isReadonlyField(element) && target.writeReadonly !== true)) {
      return;
    }

    if (element instanceof HTMLSelectElement && !selectHasOptionValue(element, "")) {
      return;
    }

    if (element.value !== "") {
      element.value = "";
      dispatchFieldEvent(element, "input");
      dispatchFieldEvent(element, "change");
    }

    this.fieldWrittenValues.delete(element);
    cleared.add(element);
  }

  private canWriteField(
    element: StreetKitFieldControl,
    options: {
      target: NormalisedFieldTarget;
      value: string;
      defaultOverwrite: StreetKitFieldOverwriteMode;
    }
  ): { allowed: boolean; dropOwnership: boolean } {
    if (element.disabled) {
      return { allowed: false, dropOwnership: false };
    }

    if (isReadonlyField(element) && options.target.writeReadonly !== true) {
      return { allowed: false, dropOwnership: false };
    }

    const overwrite = options.target.overwrite ?? options.defaultOverwrite;
    const lastValue = this.fieldWrittenValues.get(element);
    const isEmpty = element.value.trim().length === 0;
    const isStreetKitOwned = lastValue !== undefined && element.value === lastValue;

    if (overwrite === "always" || isEmpty || (overwrite === "emptyOrStreetKit" && isStreetKitOwned)) {
      return { allowed: true, dropOwnership: false };
    }

    return {
      allowed: false,
      dropOwnership: lastValue !== undefined && element.value !== lastValue
    };
  }

  private resolveFieldTarget(key: string, target: string | StreetKitFieldControl): StreetKitFieldControl | null {
    if (typeof target !== "string") {
      return target;
    }

    const elements = [...this.document.querySelectorAll(target)];

    if (elements.length !== 1) {
      this.warnFieldMapping({
        key,
        target,
        reason: elements.length === 0 ? "target_not_found" : "target_not_unique",
        message:
          elements.length === 0
            ? "StreetKit field mapping target was not found."
            : "StreetKit field mapping selector matched multiple targets."
      });
      return null;
    }

    const element = elements[0];

    if (element === undefined || !isFieldValueElement(element)) {
      this.warnFieldMapping({
        key,
        target,
        reason: "unsupported_target",
        message: "StreetKit field mapping target must be an input, textarea, or select."
      });
      return null;
    }

    return element;
  }

  private warnFieldMapping(options: {
    key: string;
    target: string | StreetKitFieldControl;
    reason: string;
    message: string;
    cause?: unknown;
  }): void {
    const selector = typeof options.target === "string" ? options.target : undefined;
    const warningKey = `${options.key}:${options.reason}:${selector ?? "element"}`;

    if (this.fieldMappingWarnings.has(warningKey)) {
      return;
    }

    this.fieldMappingWarnings.add(warningKey);
    this.config.onError?.({
      code: "field_mapping_failed",
      message: options.message,
      fieldKey: options.key,
      reason: options.reason,
      ...(selector === undefined ? {} : { selector }),
      ...(options.cause === undefined ? {} : { cause: options.cause })
    });
  }

  private warnForConfigError(error: StreetKitError): void {
    if (error.code !== "invalid_config") {
      return;
    }

    const warningKey = `${error.reason ?? "invalid_config"}:${error.message}`;

    if (this.configWarnings.has(warningKey)) {
      return;
    }

    this.configWarnings.add(warningKey);

    const warn = globalThis.console?.warn;

    if (typeof warn === "function") {
      warn.call(globalThis.console, `[StreetKit] ${error.message}`);
    }
  }

  private toRenderedSearchResult(result: RankedStreetKitCandidate): StreetKitSearchResult {
    const publicResult = toPublicSearchResult(result);

    return this.ascii
      ? {
          ...publicResult,
          label: deriveAsciiLabel(publicResult.label),
          ...(typeof publicResult.line1 === "string"
            ? { line1: deriveAsciiLabel(publicResult.line1) }
            : {}),
          ...(typeof publicResult.line2 === "string"
            ? { line2: deriveAsciiLabel(publicResult.line2) }
            : {})
        }
      : publicResult;
  }

  private displayText(value: string): string {
    return this.ascii ? deriveAsciiLabel(value) : value;
  }

  private clearTimers(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.blurTimer !== null) {
      clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
  }

  private abortActiveSearch(): void {
    this.activeSearchController?.abort();
    this.activeSearchController = null;
  }

  private optionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }
}

function createIndexClient(config: StreetKitWidgetConfig): StreetKitIndexClient {
  try {
    return new StreetKitIndexClient(config);
  } catch (error) {
    const streetkitError = toStreetKitError(error);
    config.onError?.(streetkitError);
    throw error;
  }
}

function resolveValidatedInput(config: StreetKitWidgetConfig): HTMLInputElement {
  try {
    assertValidWidgetConfig(config);
    const input = resolveInput(config.input);
    assertInputCanAttach(input, config);
    return input;
  } catch (error) {
    config.onError?.(toStreetKitError(error));
    throw error;
  }
}

function assertValidWidgetConfig(config: StreetKitWidgetConfig): void {
  assertNumericOption(config.limit, "limit", 1, MAX_STREETKIT_RENDERED_SUGGESTIONS);
  assertNumericOption(config.maxResults, "maxResults", 1, MAX_STREETKIT_RENDERED_SUGGESTIONS);
  assertNumericOption(config.minQueryLength, "minQueryLength", 1, 20);
  assertNumericOption(config.minLength, "minLength", 1, 20);
  assertNumericOption(config.debounceMs, "debounceMs", 0, 2000);

  if (
    config.siteId !== undefined &&
    config.siteId.trim().length > 0 &&
    !validateStreetKitSiteId(config.siteId.trim())
  ) {
    throwConfigError(config, "StreetKit siteId must match sk_live_* or sk_test_*.", {
      siteId: config.siteId.trim()
    });
  }

  for (const [name, callback] of Object.entries({
    onQuery: config.onQuery,
    onResults: config.onResults,
    onSearchStart: config.onSearchStart,
    onSearchComplete: config.onSearchComplete,
    onSelect: config.onSelect,
    onClear: config.onClear,
    onError: config.onError
  })) {
    if (callback !== undefined && typeof callback !== "function") {
      throwConfigError(config, `StreetKit ${name} must be a function.`);
    }
  }
}

function assertNumericOption(
  value: number | undefined,
  name: string,
  min: number,
  max: number
): void {
  if (value === undefined) {
    return;
  }

  if (!Number.isFinite(value) || value < min || value > max) {
    throw new StreetKitConfigError(
      `StreetKit ${name} must be a number from ${min} to ${max}.`
    );
  }
}

function throwConfigError(
  _config: StreetKitWidgetConfig,
  message: string,
  context: Omit<StreetKitError, "code" | "message"> = {}
): never {
  const error = new StreetKitConfigError(message, context);
  throw error;
}

function assertInputCanAttach(input: HTMLInputElement, config: StreetKitWidgetConfig): void {
  if (!isTextLikeInput(input)) {
    throwConfigError(config, "StreetKit input must be a text-like HTMLInputElement.");
  }

  if (activeWidgets.has(input)) {
    throwConfigError(config, "StreetKit input already has an attached widget.");
  }
}

function resolveInput(input: string | HTMLInputElement): HTMLInputElement {
  if (typeof input !== "string") {
    return input;
  }

  const elements = [...document.querySelectorAll(input)];

  if (elements.length !== 1) {
    throw new StreetKitConfigError(
      elements.length === 0
        ? `StreetKit input not found: ${input}`
        : `StreetKit input selector matched ${elements.length} elements: ${input}`
    );
  }

  const element = elements[0];

  if (!(element instanceof HTMLInputElement)) {
    throw new StreetKitConfigError(`StreetKit input is not an HTMLInputElement: ${input}`);
  }

  return element;
}

function isTextLikeInput(input: HTMLInputElement): boolean {
  const type = input.type === "" ? "text" : input.type.toLowerCase();
  return textLikeInputTypes.has(type);
}

const textLikeInputTypes = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url"
]);

const widgetOwnedInputAttributes = [
  "autocomplete",
  "role",
  "aria-autocomplete",
  "aria-haspopup",
  "aria-controls",
  "aria-expanded",
  "aria-activedescendant",
  "aria-disabled"
] as const;

function captureInputAttributes(input: HTMLInputElement): Map<string, string | null> {
  const attributes = new Map<string, string | null>();

  for (const attribute of widgetOwnedInputAttributes) {
    attributes.set(attribute, input.getAttribute(attribute));
  }

  return attributes;
}

function restoreInputAttributes(
  input: HTMLInputElement,
  attributes: Map<string, string | null>
): void {
  for (const attribute of widgetOwnedInputAttributes) {
    const value = attributes.get(attribute) ?? null;

    if (value === null) {
      input.removeAttribute(attribute);
    } else {
      input.setAttribute(attribute, value);
    }

    if (attribute === "autocomplete") {
      (input as unknown as { autocomplete: string }).autocomplete = value ?? "";
    }
  }
}

const streetKitBuiltInFieldKeys = [
  "label",
  "fullLabel",
  "line1",
  "line2",
  "unit",
  "number",
  "streetNumber",
  "street",
  "streetName",
  "streetType",
  "streetSuffix",
  "suburb",
  "city",
  "townCity",
  "postcode",
  "territorialAuthority",
  "region",
  "country",
  "linzId",
  "id",
  "streetkitId",
  "sourceProvider",
  "sourceDataset",
  "sourceLayer",
  "sourceVersion",
  "datasetVersion",
  "latitude",
  "longitude",
  "lat",
  "lon"
] as const satisfies readonly StreetKitBuiltInFieldKey[];

const metadataFieldKeys = new Set<StreetKitBuiltInFieldKey>([
  "linzId",
  "id",
  "streetkitId",
  "sourceProvider",
  "sourceDataset",
  "sourceLayer",
  "sourceVersion",
  "datasetVersion",
  "latitude",
  "longitude",
  "lat",
  "lon"
]);

type NormalisedFieldTarget = {
  target: string | StreetKitFieldControl;
  overwrite?: StreetKitFieldOverwriteMode;
  clear?: boolean;
  writeReadonly?: boolean;
};

function buildFieldValues(
  address: StreetKitSelectedAddress,
  options: {
    ascii: boolean;
    lineMode: StreetKitFieldMappingOptions["lineMode"];
    separateUnit: boolean;
  }
): Record<StreetKitBuiltInFieldKey, string> {
  const number = selectedAddressNumber(address) ?? "";
  const street = selectedAddressStreet(address) ?? "";
  const unit = address.components.unit ?? "";
  const unitLine = unit.length > 0 ? `Unit ${unit}` : "";
  const streetLine = [number, street].filter((part) => part.length > 0).join(" ");
  const unitInLine1 =
    unit.length > 0 &&
    (options.lineMode === "unitInLine1" ||
      (options.lineMode === "auto" && options.separateUnit === false));
  const line1 = unitInLine1 && streetLine.length > 0 ? `${unit}/${streetLine}` : streetLine;
  const line2 = unit.length > 0 && options.lineMode !== "unitInLine1" ? unitLine : "";
  const hasCoordinates =
    typeof address.location.lat === "number" && typeof address.location.lon === "number";
  const values: Record<StreetKitBuiltInFieldKey, string> = {
    label: address.label,
    fullLabel: address.label,
    line1,
    line2,
    unit,
    number,
    streetNumber: number,
    street,
    streetName: address.components.streetName ?? "",
    streetType: address.components.streetType ?? "",
    streetSuffix: address.components.streetSuffix ?? "",
    suburb: address.components.suburb ?? "",
    city: address.components.city ?? "",
    townCity: address.components.city ?? "",
    postcode: address.components.postcode ?? "",
    territorialAuthority: address.components.territorialAuthority ?? "",
    region: address.components.region ?? "",
    country: address.components.country,
    linzId: address.id,
    id: address.id,
    streetkitId: address.id,
    sourceProvider: address.source.provider,
    sourceDataset: address.source.dataset,
    sourceLayer: address.source.layer,
    sourceVersion: address.source.version,
    datasetVersion: address.source.version,
    latitude: hasCoordinates ? String(address.location.lat) : "",
    longitude: hasCoordinates ? String(address.location.lon) : "",
    lat: hasCoordinates ? String(address.location.lat) : "",
    lon: hasCoordinates ? String(address.location.lon) : ""
  };

  if (!options.ascii) {
    return values;
  }

  return Object.fromEntries(
    streetKitBuiltInFieldKeys.map((key) => [key, deriveAsciiLabel(values[key])])
  ) as Record<StreetKitBuiltInFieldKey, string>;
}

function fieldTargetConfigured(
  target: StreetKitFieldTarget | undefined
): target is StreetKitFieldTarget {
  return target !== undefined;
}

function normaliseFieldTarget(
  target: StreetKitFieldTarget | StreetKitCustomFieldMapping | undefined
): NormalisedFieldTarget | null {
  if (target === undefined) {
    return null;
  }

  if (isFieldTargetOptions(target)) {
    return {
      target: target.target,
      ...("overwrite" in target && target.overwrite !== undefined
        ? { overwrite: target.overwrite }
        : {}),
      ...("clear" in target && target.clear !== undefined ? { clear: target.clear } : {}),
      ...("writeReadonly" in target && target.writeReadonly !== undefined
        ? { writeReadonly: target.writeReadonly }
        : {})
    };
  }

  return { target };
}

function isFieldTargetOptions(
  target: StreetKitFieldTarget | StreetKitCustomFieldMapping
): target is NormalisedFieldTarget {
  return typeof target === "object" && target !== null && "target" in target;
}

function selectedAddressNumber(address: StreetKitSelectedAddress): string | null {
  const number = address.components.number;

  if (number === null) {
    return null;
  }

  const low = `${number}${address.components.numberSuffix ?? ""}`;
  return address.components.numberHigh === null ? low : `${low}-${address.components.numberHigh}`;
}

function selectedAddressStreet(address: StreetKitSelectedAddress): string | null {
  if (hasText(address.components.street)) {
    return address.components.street;
  }

  const parts = [
    address.components.streetName,
    address.components.streetType,
    address.components.streetSuffix
  ].filter(hasText);

  return parts.length === 0 ? null : parts.join(" ");
}

function statusForPlan(
  plan: StreetKitSearchPlan,
  results: StreetKitSearchResult[]
): StreetKitSearchCompleteStatus {
  if (plan.status === "too_broad") {
    return "too_broad";
  }

  if (plan.status === "too_short") {
    return "too_short";
  }

  if (plan.status === "empty" || results.length === 0) {
    return "empty";
  }

  return "success";
}

function streetKitErrorForSearch(error: unknown, requestId: number): StreetKitError {
  const baseError = toStreetKitError(error);
  const code = searchErrorCode(error, baseError.code);

  return {
    ...baseError,
    code,
    message: baseError.code === "invalid_config" ? baseError.message : searchErrorMessage(code),
    requestId
  };
}

function toStreetKitError(error: unknown): StreetKitError {
  if (error instanceof StreetKitConfigError) {
    return error.streetkitError;
  }

  if (isStreetKitError(error)) {
    return error;
  }

  if (error instanceof StreetKitHttpError) {
    return {
      code: "unknown",
      message: error.message,
      status: error.status,
      url: error.url,
      cause: error
    };
  }

  if (error instanceof Error) {
    return {
      code: "unknown",
      message: error.message,
      cause: error
    };
  }

  return {
    code: "unknown",
    message: "StreetKit encountered an unknown error.",
    cause: error
  };
}

function searchErrorCode(
  error: unknown,
  fallback: StreetKitError["code"]
): StreetKitError["code"] {
  const url = error instanceof StreetKitHttpError ? error.url : "";

  if (fallback === "invalid_config") {
    return fallback;
  }

  if (/\/meta\.json(?:[?#]|$)/u.test(url) || /\/manifest\.json(?:[?#]|$)/u.test(url)) {
    return "metadata_failed";
  }

  if (/\/shard-routing\.json(?:[?#]|$)/u.test(url)) {
    return "routing_failed";
  }

  if (/\/shards\//u.test(url)) {
    return "shard_failed";
  }

  return fallback === "unknown" ? "metadata_failed" : fallback;
}

function searchErrorMessage(code: StreetKitError["code"]): string {
  switch (code) {
    case "invalid_config":
      return "StreetKit configuration is invalid.";
    case "metadata_failed":
      return "StreetKit index metadata is unavailable.";
    case "routing_failed":
      return "StreetKit shard routing is unavailable.";
    case "shard_failed":
      return "StreetKit address shard is unavailable.";
    case "detail_failed":
      return "Selected address detail is unavailable.";
    case "field_mapping_failed":
      return "StreetKit field mapping failed.";
    case "selection_failed":
      return "StreetKit address selection failed.";
    case "unknown":
      return "StreetKit address search is unavailable.";
  }
}

function isStreetKitError(error: unknown): error is StreetKitError {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code?: unknown }).code === "string" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  );
}

function streetKitError(
  code: StreetKitError["code"],
  message: string,
  cause?: unknown
): StreetKitError {
  if (cause instanceof StreetKitHttpError) {
    return { code, message, status: cause.status, url: cause.url, cause };
  }

  return cause === undefined ? { code, message } : { code, message, cause };
}

function dispatchFieldEvent(element: HTMLElement, type: "input" | "change"): void {
  const event = createFieldEvent(element, type);

  try {
    element.dispatchEvent(event);
  } catch {
    // Some test DOMs do not implement the browser Event constructor fully.
  }
}

function createFieldEvent(element: HTMLElement, type: "input" | "change"): Event {
  const eventConstructor = element.ownerDocument.defaultView?.Event;

  if (typeof eventConstructor === "function") {
    return new eventConstructor(type, { bubbles: true });
  }

  let defaultPrevented = false;

  return {
    type,
    bubbles: true,
    cancelable: false,
    get defaultPrevented() {
      return defaultPrevented;
    },
    preventDefault() {
      defaultPrevented = true;
    }
  } as Event;
}

function appendRenderedText(options: {
  document: Document;
  element: HTMLElement;
  text: string;
  query: string;
  highlight: boolean;
  highlightClass: string;
}): void {
  if (!options.highlight) {
    options.element.textContent = options.text;
    return;
  }

  const ranges = highlightRanges(options.text, options.query);

  if (ranges.length === 0) {
    options.element.textContent = options.text;
    return;
  }

  let cursor = 0;

  for (const range of ranges) {
    if (range.start > cursor) {
      options.element.append(options.document.createTextNode(options.text.slice(cursor, range.start)));
    }

    const mark = options.document.createElement("span");
    mark.className = options.highlightClass;
    mark.textContent = options.text.slice(range.start, range.end);
    options.element.append(mark);
    cursor = range.end;
  }

  if (cursor < options.text.length) {
    options.element.append(options.document.createTextNode(options.text.slice(cursor)));
  }
}

function highlightRanges(text: string, query: string): Array<{ start: number; end: number }> {
  const foldedText = deriveAsciiLabel(text).toLowerCase();
  const tokens = normaliseAddressQuery(query)
    .split(" ")
    .filter((token) => token.length >= 2 && !/^\d+$/u.test(token));
  const ranges: Array<{ start: number; end: number }> = [];

  for (const token of tokens) {
    let index = foldedText.indexOf(token);

    while (index >= 0) {
      ranges.push({ start: index, end: index + token.length });
      index = foldedText.indexOf(token, index + token.length);
    }
  }

  return mergeRanges(ranges);
}

function mergeRanges(ranges: Array<{ start: number; end: number }>): Array<{ start: number; end: number }> {
  const sorted = [...ranges].sort((left, right) => left.start - right.start || left.end - right.end);
  const merged: Array<{ start: number; end: number }> = [];

  for (const range of sorted) {
    const previous = merged.at(-1);

    if (previous !== undefined && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end);
      continue;
    }

    merged.push({ ...range });
  }

  return merged;
}

function isFieldValueElement(
  element: Element
): element is StreetKitFieldControl {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  );
}

function isReadonlyField(element: StreetKitFieldControl): boolean {
  return (
    (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
    element.readOnly
  );
}

function selectHasOptionValue(element: HTMLSelectElement, value: string): boolean {
  return [...element.options].some((option) => option.value === value);
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function normaliseRenderedLimit(value: number | undefined): number {
  const limit = value ?? DEFAULT_STREETKIT_LIMIT;

  if (!Number.isFinite(limit)) {
    return DEFAULT_STREETKIT_LIMIT;
  }

  return clamp(Math.floor(limit), 1, MAX_STREETKIT_RENDERED_SUGGESTIONS);
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: unknown }).name === "AbortError"
  );
}
